import { parseSupportedCurrencies } from "@/integrations/fx/providers";
import { type FxRateRepository } from "@/repositories/interfaces";
import {
  type FxRateManager,
  type FxRate,
  type ISO4217,
  type CurrencyPair,
  type DateRange,
  type RefreshResult,
  type ValidationResult,
  type RateCriteria,
} from "@/types/fx";

type CacheKey = string;

function key(from: ISO4217, to: ISO4217, date: Date): CacheKey {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return `${from}->${to}@${d.toISOString().slice(0, 10)}`;
}

export class SupabaseFxRateManager implements FxRateManager {
  private repo: FxRateRepository;
  private l1Cache = new Map<CacheKey, FxRate>();

  constructor(repo: FxRateRepository) {
    this.repo = repo;
  }

  async getHistoricalRate(
    from: ISO4217,
    to: ISO4217,
    date: Date,
  ): Promise<FxRate> {
    const k = key(from, to, date);
    const cached = this.l1Cache.get(k);
    if (cached) return cached;

    const rate = await this.repo.getHistoricalRate(from, to, date);
    if (!rate) {
      throw new Error(
        `No historical rate available for ${from}->${to} on ${new Date(date)
          .toISOString()
          .slice(0, 10)}`,
      );
    }
    this.l1Cache.set(k, rate);
    return rate;
  }

  async getCurrentRate(from: ISO4217, to: ISO4217): Promise<FxRate> {
    const today = startOfDay(new Date());
    const k = key(from, to, today);
    const cached = this.l1Cache.get(k);
    if (cached) return cached;

    const rate = await this.repo.getCurrentRate(from, to);
    if (!rate) {
      throw new Error(`No current rate available for ${from}->${to}`);
    }
    this.l1Cache.set(k, rate);
    return rate;
  }

  async getHistoricalRatesFor(
    pairs: CurrencyPair[],
    dateRange: DateRange,
  ): Promise<FxRate[]> {
    const rates = await this.repo.getHistoricalRatesFor(pairs, dateRange);
    for (const r of rates) {
      this.l1Cache.set(key(r.baseCurrency, r.targetCurrency, r.date), r);
    }
    return rates;
  }

  async refreshRates(
    source?: string,
    forceUpdate?: boolean,
  ): Promise<RefreshResult> {
    const errors: string[] = [];
    const supported = parseSupportedCurrencies();
    const bases = (supported.length ? supported : ["USD", "EUR", "GBP"]).map(
      (c) => c.toUpperCase(),
    );
    const today = startOfDay(new Date());

    let fetched = 0;
    let stored = 0;
    let usedSource = "edge-function";

    try {
      // Import Supabase client dynamically to avoid circular dependencies
      const { supabase } = await import("@/integrations/supabase/client");

      // Call Edge Function for each base in the supported list and aggregate results
      const allRates: FxRate[] = [];

      for (const base of bases) {
        try {
          const { data, error } = await supabase.functions.invoke(
            "fx-rates-fetcher",
            {
              body: { forceRefresh: forceUpdate, baseCurrency: base },
            },
          );

          if (error) {
            errors.push(
              `Edge Function error for base ${base}: ${error.message}`,
            );
            continue;
          }

          if (data && data.rates) {
            // Record the provider/source returned by the edge function for visibility
            usedSource = `edge-function-${data.source}`;

            // Convert the rates object to FxRate array for this base
            for (const [targetCurrency, rate] of Object.entries(data.rates)) {
              if (targetCurrency !== base && typeof rate === "number") {
                allRates.push({
                  baseCurrency: base as ISO4217,
                  targetCurrency: targetCurrency as ISO4217,
                  rate: rate,
                  date: today,
                  source: usedSource,
                });
                fetched++;
              }
            }
          }
        } catch (innerErr: unknown) {
          const msg =
            innerErr instanceof Error ? innerErr.message : String(innerErr);
          errors.push(
            `Edge Function invocation failed for base ${base}: ${msg}`,
          );
        }
      }

      if (allRates.length > 0) {
        await this.storeRates(allRates);
        stored = allRates.length;
      }
    } catch (e: unknown) {
      const msg =
        e instanceof Error
          ? e.message
          : typeof e === "string"
            ? e
            : "Unknown error";
      errors.push(`Edge Function call failed: ${msg}`);
    }

    return {
      source: usedSource,
      fetched,
      stored,
      errors,
    };
  }

  async validateRateConsistency(rates: FxRate[]): Promise<ValidationResult> {
    const warnings: string[] = [];
    for (const r of rates) {
      if (!(r.rate > 0) || !Number.isFinite(r.rate)) {
        warnings.push(
          `Non-positive or invalid rate for ${r.baseCurrency}->${r.targetCurrency} on ${new Date(
            r.date,
          )
            .toISOString()
            .slice(0, 10)}: ${r.rate}`,
        );
      }
    }
    return {
      ok: warnings.length === 0,
      warnings: warnings.length ? warnings : undefined,
    };
  }

  async getCachedRates(criteria: RateCriteria): Promise<FxRate[]> {
    const repoRates = await this.repo.getCachedRates(criteria);

    // Merge with L1 cache
    const l1: FxRate[] = Array.from(this.l1Cache.values());

    // Deduplicate by (base, target, date)
    const seen = new Set<string>();
    const merged: FxRate[] = [];
    const add = (r: FxRate) => {
      const d = startOfDay(new Date(r.date));
      const k = `${r.baseCurrency}-${r.targetCurrency}-${d.toISOString().slice(0, 10)}`;
      if (!seen.has(k)) {
        seen.add(k);
        merged.push(r);
      }
    };

    for (const r of repoRates) add(r);
    for (const r of l1) add(r);

    // Final filter pass for any remaining criteria
    return merged.filter((r) => {
      if (criteria.baseCurrency && r.baseCurrency !== criteria.baseCurrency)
        return false;
      if (
        criteria.targetCurrency &&
        r.targetCurrency !== criteria.targetCurrency
      )
        return false;
      if (criteria.source && r.source !== criteria.source) return false;
      if (criteria.date) {
        const d = startOfDay(criteria.date);
        const rd = startOfDay(new Date(r.date));
        if (rd.getTime() !== d.getTime()) return false;
      }
      if (
        criteria.startDate &&
        new Date(r.date) < startOfDay(criteria.startDate)
      )
        return false;
      if (criteria.endDate && new Date(r.date) > endOfDay(criteria.endDate))
        return false;
      return true;
    });
  }

  async storeRates(rates: FxRate[]): Promise<void> {
    await this.repo.upsertRates(rates);
    for (const r of rates) {
      this.l1Cache.set(key(r.baseCurrency, r.targetCurrency, r.date), r);
    }
  }
}

function startOfDay(d: Date): Date {
  const nd = new Date(d);
  nd.setHours(0, 0, 0, 0);
  return nd;
}
function endOfDay(d: Date): Date {
  const nd = new Date(d);
  nd.setHours(23, 59, 59, 999);
  return nd;
}
